import React from 'react';
import { useCreateAccount } from '../hooks/useCreateAccount';

export default function CreateAccountPage({ onBackToLogin }: { onBackToLogin: () => void }) {
  const {
    formData,
    error,
    loading,
    accountCreated,
    passwordPolicy,
    isPasswordValid,
    passwordsMatch,
    isPhoneValid,
    updateFormData,
    handleSubmit
  } = useCreateAccount();

  if (accountCreated) {
    return (
      <div className="create-account-container">
        <div className="create-account-logo-top">
          <img src="/logo.png" alt="CaseKaizen Logo" className="logo-img" />
        </div>
        <div className="create-account-content">
          <div className="create-account-form-wrapper">
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h2>Account Created Successfully!</h2>
              <p>We've sent a confirmation email to <strong>{formData.email}</strong>.</p>
              <p>Please check your email and click the confirmation link to activate your account.</p>
              <div className="email-notes">
                <p><strong>Note:</strong></p>
                <ul>
                  <li>Check your spam folder if you don't see the email</li>
                  <li>The confirmation link will expire in 24 hours</li>
                  <li>You can request a new confirmation email if needed</li>
                </ul>
              </div>
              <button type="button" onClick={onBackToLogin} className="back-to-login-btn">
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-account-container">
      <div className="create-account-logo-top">
        <img src="/logo.png" alt="CaseKaizen Logo" className="logo-img" />
      </div>
      <div className="create-account-content">
        <div className="create-account-form-wrapper">
        <h2>Create Account</h2>
        <p className="instruction-text">Please enter your details below.</p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input
              id="username"
              type="text"
              value={formData.username}
              onChange={e => updateFormData('username', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={e => updateFormData('email', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number:</label>
            <input
              id="phone"
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={formData.phone}
              onChange={e => updateFormData('phone', e.target.value)}
              required
              style={{
                borderColor: formData.phone && !isPhoneValid ? '#dc3545' : undefined
              }}
            />
            {formData.phone && !isPhoneValid && (
              <div className="error-message" style={{ marginTop: '4px', fontSize: '0.9em' }}>
                Please enter a valid phone number (e.g., +1 555-123-4567)
              </div>
            )}
            {(!formData.phone || isPhoneValid) && (
              <small style={{ color: '#666', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
                Include country code (e.g., +1 for US/Canada)
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <input
              id="password"
              type="password"
              value={formData.password}
              onChange={e => updateFormData('password', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Retype Password:</label>
            <input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={e => updateFormData('confirmPassword', e.target.value)}
              required
            />
            {formData.confirmPassword && !passwordsMatch && (
              <div className="error-message">Passwords do not match.</div>
            )}
          </div>

          <div className="password-policy-box">
            <h4>Password Requirements:</h4>
            <ul>
              <li className={passwordPolicy.minLength ? 'valid' : 'invalid'}>
                Password must be at least 8 characters long.
              </li>
              <li className={passwordPolicy.maxLength ? 'valid' : 'invalid'}>
                Password must not be longer than 18 characters.
              </li>
              <li className={passwordPolicy.hasUpperCase && passwordPolicy.hasLowerCase ? 'valid' : 'invalid'}>
                Password must contain at least one upper case and one lower case letter.
              </li>
              <li className={passwordPolicy.hasNumberOrPunctuation ? 'valid' : 'invalid'}>
                Password must contain at least one number or punctuation character.
              </li>
              <li className={passwordPolicy.noSpacesOrUnicode ? 'valid' : 'invalid'}>
                Password must not contain space or unicode characters.
              </li>
            </ul>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="button-group">
            <button type="submit" disabled={loading || !isPasswordValid || !passwordsMatch || !isPhoneValid}>
              {loading ? 'Creating Account...' : 'Submit'}
            </button>
            <button type="button" onClick={onBackToLogin} className="cancel-btn">
              Cancel
            </button>
          </div>
        </form>

        <div className="login-link">
          Already have an account? <a href="#" onClick={onBackToLogin}>Sign In</a>
        </div>
        </div>
      </div>
    </div>
  );
} 